import { Component, ComponentFactoryResolver, ComponentRef, Injector, OnDestroy, OnInit, ViewChild, ViewContainerRef } from "@angular/core";
import { PastValueViewerService } from "ng/src/app/notes/past-values/past-value-viewer/past-value-viewer.service";
import { fromEvent, Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { PastValueDetails, PastValueHistoryItem } from "ng/src/app/notes/past-values/past-value-viewer/past-value-viewer.model";
import { PastValuesComponent } from "ng/src/app/notes/past-values/past-values.component";
import { Dialog } from "primeng/dialog";
import { DatePipe } from "@angular/common";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { NoteService } from "ng/src/app/notes/shared/note.service";

@Component({
	selector: 'lb-past-value-container',
	templateUrl: './past-value-viewer.component.html',
	styleUrls: ['./past-value-viewer.component.scss'],
	providers: [DatePipe]
})
export class PastValueViewerComponent implements OnInit, OnDestroy {
	@ViewChild('dialog') dialog!: Dialog;
	@ViewChild('container', { read: ViewContainerRef }) container!: ViewContainerRef;
	
	history: PastValueHistoryItem[] = [];
	
	get isMobileSize() {
		return window.innerWidth <= 768
	}
	
	get currentComponent() {
		return this._currentComponent;
	}
	
	set currentComponent(val) {
		this._currentComponent = val;
		for (let i = this.container.length - 1; i >= 0; i--) {
			this.container.detach(i);
		}
		if (!this._currentComponent) return;
		this.container.insert(this._currentComponent.hostView);
	}
	
	get currentInstance() {
		return this.currentComponent?.instance;
	}
	
	get canGoBack() {
		return this.history.length > 0 || this.currentComponent?.instance?.viewState === 'NOTE';
	}
	
	private _currentComponent?: ComponentRef<PastValuesComponent>;
	private readonly onDestroy = new Subject();
	
	constructor(
		private pastValueService: PastValueViewerService,
		private injector: Injector,
		private componentFactory: ComponentFactoryResolver,
		private datePipe: DatePipe,
		private appStateService: AppStateService,
		private noteService: NoteService
	) {}
	
	ngOnInit() {
		this.pastValueService.pastValueSubject.pipe(
				takeUntil(this.onDestroy)
			)
			.subscribe((details) => {
				this.addComponent(details);
			});
		
		fromEvent(window, 'keydown')
			.pipe(
				takeUntil(this.onDestroy),
				filter(event => !!this.currentComponent && (event as KeyboardEvent).key === 'Escape')
			)
			.subscribe(() => this.close());
		
		this.appStateService.locationChanged$.pipe(
			takeUntil(this.onDestroy)
		).subscribe(() => this.close());
	}
	
	ngOnDestroy() {
		this.onDestroy.next();
	}
	
	addComponent(details: PastValueDetails) {
		const factory = this.componentFactory.resolveComponentFactory(PastValuesComponent);
		const comp = factory.create(this.injector);
		
		comp.instance.note = details.note;
		comp.instance.templateSegment = details.segment;
		comp.instance.showContextValue = true;
		comp.instance.overlayNote = false;
		comp.instance.maxNoteHeight = '';
		comp.instance.displayTickLabels = true;
		comp.instance.refreshOnUpdate = 'ref';
		
		this.noteService.register(details.note.id).pipe(
			takeUntil(comp.instance.onDestroy$)
		)
			.subscribe((note) => comp.instance.note = note);
		
		const old = this.currentComponent;
		this.currentComponent = comp;
		if (!old) return;
		this.history.splice(0, 0, this.createHistoryEntry(old));
	}
	
	close() {
		this.container.clear();
		this.currentComponent = undefined;
		this.destroyHistory();
	}
	
	resetInstanceState() {
		if (this.currentInstance) {
			this.currentInstance.viewState = 'VALUES';
			this.currentInstance.noteToDetail = undefined;
		}
	}
	
	goBack() {
		if (this.currentInstance?.viewState === 'NOTE') {
			this.resetInstanceState();
		}
		else {
			this.container.clear();
			this.currentComponent = this.history.splice(0, 1)[0]?.item;
			this.resetInstanceState();
		}
	}
	
	navigateTo(comp: ComponentRef<any>) {
		const idx = this.history.findIndex(entry => entry.item === comp);
		const spliced = this.history.splice(0, idx + 1).reverse();
		spliced.forEach((entry, idx) => {
			if (idx === 0) return;
			entry.item.destroy();
		});
		this.currentComponent = spliced[0]?.item;
		this.resetInstanceState();
	}
	
	private destroyHistory() {
		this.history.forEach(entry => entry.item.destroy());
		this.history = [];
	}
	
	private createHistoryEntry(comp: ComponentRef<PastValuesComponent>): PastValueHistoryItem {
		return {
			label: `${this.datePipe.transform(comp.instance.note.noteTime, 'MM/dd/yyyy HH:mm') ?? ''} ${comp.instance.templateSegment.longName}`,
			item: comp,
			command: () => this.navigateTo(comp)
		}
	}
}